
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useCreateCompany, useUpdateCompany, useDeleteCompany } from "@/hooks/useCompanies";
import { Company } from "@/types/company";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { CompanyFormFields } from "./CompanyDialog/CompanyFormFields";
import { CompanyLogoUpload } from "./CompanyDialog/CompanyLogoUpload";
import { CompanyDialogActions } from "./CompanyDialog/CompanyDialogActions";

interface CompanyDialogProps {
  company?: Company;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: () => void;
}

export const CompanyDialog = ({ company, trigger, open: externalOpen, onOpenChange, onSuccess }: CompanyDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [formData, setFormData] = useState({
    name_ar: "",
    mobile: "",
    address_ar: "",
  });
  const [isUploading, setIsUploading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const deleteCompany = useDeleteCompany();

  useEffect(() => {
    if (open) {
      setFormData({
        name_ar: company?.name_ar || "",
        mobile: company?.mobile || "",
        address_ar: company?.address_ar || "",
      });
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [open, company]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const uploadLogo = async (companyId: string, file: File) => {
    setIsUploading(true);
    try {
        const filePath = `public/${companyId}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
            .from('company-logos')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
            .from('company-logos')
            .getPublicUrl(filePath);

        await updateCompany.mutateAsync({
            id: companyId,
            logo_url: publicUrl,
        });

        toast.success("تم تحديث شعار الشركة بنجاح.");
    } catch (error) {
        console.error("Error uploading logo:", error);
        toast.error("فشل في تحميل الشعار.");
        // Re-throw the error to be caught by the handleSubmit's catch block if needed
        throw error;
    } finally {
        setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (company) {
        await updateCompany.mutateAsync({ id: company.id, ...formData });
        if (logoFile) {
          await uploadLogo(company.id, logoFile);
        }
        toast.success("تم تحديث الشركة بنجاح");
      } else {
        const newCompany = await createCompany.mutateAsync(formData);
        if (logoFile && newCompany) {
          await uploadLogo(newCompany.id, logoFile);
        }
        toast.success("تم إضافة الشركة بنجاح");
      }
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error("حدث خطأ أثناء حفظ البيانات");
    }
  };

  const handleDelete = async () => {
    if (!company) return;
    
    try {
      await deleteCompany.mutateAsync(company.id);
      toast.success("تم حذف الشركة بنجاح");
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error("حدث خطأ أثناء حذف الشركة");
    }
  };

  const handleFormChange = (data: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const isSubmitting = isUploading || createCompany.isPending || updateCompany.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{company ? "تعديل الشركة" : "إضافة شركة جديدة"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <CompanyFormFields formData={formData} onFormChange={handleFormChange} />
          <CompanyLogoUpload 
            logoPreview={logoPreview}
            companyLogoUrl={company?.logo_url}
            onFileChange={handleFileChange}
            isUploading={isUploading}
          />
          <CompanyDialogActions
            company={company}
            isSubmitting={isSubmitting}
            onDelete={handleDelete}
            onCancel={() => setOpen(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
